import { useEffect, useState } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import ProductPage from "./pages/ProductPage";
import ProductsPage from "./pages/ProductsPage";
import BrandsPage from "./pages/BrandsPage";
import BrandPage from "./pages/BrandPage";
import AdminApp from "./admin/AdminApp";
import "@/App.css";
import { motion } from "framer-motion";
import { CartProvider } from "./context/CartContext";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Marquee } from "./components/Marquee";
import { HomeSections } from "./components/HomeSections";
import { Manifesto } from "./components/Manifesto";
import { AiRecommender } from "./components/AiRecommender";
import { DosageCalculator } from "./components/DosageCalculator";
import { Testimonials } from "./components/Testimonials";
import { BestSellers } from "./components/BestSellers";
import { CartDrawer } from "./components/CartDrawer";
import { Footer } from "./components/Footer";
import { WhatsAppFloat } from "./components/WhatsAppFloat";

const PromoBar = () => {
  const [promo, setPromo] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`)
      .then((r) => r.json())
      .then((c) => { if (c.promo_active && c.promo_text) setPromo(c.promo_text); })
      .catch(() => {});
  }, []);
  if (!promo) return null;
  return (
    <div data-testid="promo-bar" className="fixed top-0 left-0 right-0 z-[60] bg-military-dark text-baby text-center text-[11px] uppercase tracking-[0.2em] font-semibold py-2 px-4">
      {promo}
    </div>
  );
};

function Home() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App grain bg-offwhite" data-testid="app-root">
        <PromoBar />
        <Navbar />
        <main>
          <Hero />
          <Marquee />
          <HomeSections />
          <Manifesto />
          <AiRecommender />
          <DosageCalculator />
          <Testimonials />
          <BestSellers />
        </main>
        <Footer />
      </div>
  );
}

function ComingSoon() {
  return (
    <div data-testid="prelaunch-page" className="min-h-screen bg-military-dark grain flex flex-col items-center justify-center px-6 text-center relative overflow-hidden">
      <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-baby/10 blur-3xl pointer-events-none" />
      <motion.p
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        className="font-serif italic text-3xl md:text-4xl text-offwhite mb-10"
      >
        leve <span className="not-italic font-semibold text-baby">essenza</span>
      </motion.p>
      <h1 className="font-serif text-offwhite leading-[0.95] tracking-tight mb-8">
        {["Em", "breve."].map((w, i) => (
          <span key={w} className="block overflow-hidden pb-1">
            <motion.span
              className="block text-6xl md:text-[9vw] font-semibold"
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1], delay: 0.3 + i * 0.15 }}
            >
              {w}
            </motion.span>
          </span>
        ))}
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.8 }}
        data-testid="prelaunch-message"
        className="text-offwhite/60 text-base md:text-lg max-w-md leading-relaxed mb-12"
      >
        Estamos preparando algo especial para você.
      </motion.p>
      <motion.a
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        data-testid="prelaunch-whatsapp"
        href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent("Olá! Quero ser avisado quando a leve essenza abrir.")}`}
        target="_blank"
        rel="noopener noreferrer"
        className="rounded-full bg-baby text-military-dark px-8 py-4 text-sm uppercase tracking-widest font-semibold hover:bg-offwhite transition-colors duration-300"
      >
        Avise-me no WhatsApp
      </motion.a>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 text-[10px] uppercase tracking-[0.3em] text-offwhite/30"
      >
        Londrina · PR
      </motion.p>
    </div>
  );
}

function App() {
  const [prelaunch, setPrelaunch] = useState(false);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`)
      .then((r) => r.json())
      .then((c) => setPrelaunch(!!c.prelaunch_mode))
      .catch(() => {});
  }, []);
  return (
    <CartProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={prelaunch ? <ComingSoon /> : <Home />} />
          <Route path="/produtos" element={prelaunch ? <ComingSoon /> : <ProductsPage />} />
          <Route path="/marcas" element={prelaunch ? <ComingSoon /> : <BrandsPage />} />
          <Route path="/marca/:slug" element={prelaunch ? <ComingSoon /> : <BrandPage />} />
          <Route path="/produto/:slug" element={prelaunch ? <ComingSoon /> : <ProductPage />} />
          <Route path="/admin/*" element={<AdminApp />} />
        </Routes>
        <CartDrawer />
        <WhatsAppFloat />
        <Toaster position="bottom-left" toastOptions={{ style: { background: "#2B3626", color: "#F7F7F5", border: "none", borderRadius: "999px" } }} />
      </BrowserRouter>
    </CartProvider>
  );
}

export default App;
